import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import type { ExtendedProfile } from '../../../hooks/profile';

dayjs.extend(utc);
dayjs.extend(timezone);

/** S3-style folder name: UTC instant as `YYYYMMDDTHHmmssZ` (e.g. `20260322T140000Z`). */
const COMPACT_UTC_SEGMENT = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/i;

function parseCompactUtcSegment(segment: string): dayjs.Dayjs | null {
    const m = COMPACT_UTC_SEGMENT.exec(segment.trim());
    if (!m) {
        return null;
    }
    const [, y, mo, d, h, mi, s] = m;
    const djs = dayjs.utc(`${y}-${mo}-${d}T${h}:${mi}:${s}Z`);
    return djs.isValid() ? djs : null;
}

/**
 * If `prefix` or its last path segment matches compact UTC form, format in the user's timezone
 * using their profile datetime format; otherwise return `prefix` unchanged.
 */
export function formatUniformPrefixForDisplay(prefix: string, profile: ExtendedProfile): string {
    if (!prefix) {
        return prefix;
    }
    const segments = prefix.split('/').filter(Boolean);
    const lastSegment = segments.length > 0 ? segments[segments.length - 1] : prefix;
    const parsed = parseCompactUtcSegment(lastSegment) ?? parseCompactUtcSegment(prefix);
    if (!parsed) {
        return prefix;
    }
    const tz = profile.timezone?.trim() || dayjs.tz.guess() || 'UTC';
    try {
        return parsed.tz(tz).format(profile.dateTimeFormat);
    } catch {
        return parsed.utc().format(profile.dateTimeFormat);
    }
}
